import React, { useContext } from "react"
import Slider, { Settings as IReactSlickSettings } from "react-slick"
import { css } from "styled-components"
import { UIContext } from "../../components/shared/layout"
import { Spacer } from "../atoms/layout-primitives"
import VariantContainer, { IStyleVariant } from "../atoms/variant-container"
import mergeVariants from "../util/merge-variants"

interface ICardSliderProps {
  children: React.ReactNode | React.ReactNode[]
  settings?: IReactSlickSettings
  variants?: IStyleVariant | any[]
  align?: "left" | "center"
  dotsHeight?: string
}

/**
 * Slider which makes room for cards with box-shadows
 * @param settings - Optional react-slick settings override
 * @param variants - CSS Variants
 * @param align - Align the cards to the left or center when there isn't a full carousel
 * @param dotsHeight - Distance from the bottom of the carousel to the dots
 */
const CardSlider = ({
  children,
  settings,
  variants,
  align = "center",
  dotsHeight = "32px",
}: ICardSliderProps) => {
  const {
    state: { breakpoint, clientLoaded },
  } = useContext(UIContext)

  const slidesToShow = {
    sm: 1,
    md: 2,
    lg: 3,
  }

  const numberOfSlides: number = Array.isArray(children) ? children.length : 1

  const defaultSettings: IReactSlickSettings = {
    dots: true,
    infinite: true,
    arrows: false,
    slidesToShow:
      numberOfSlides > slidesToShow[breakpoint]
        ? slidesToShow[breakpoint]
        : numberOfSlides,
  }

  return (
    <>
      {/* Ensure client has loaded */}
      {clientLoaded ? (
        // For left align use css grid
        align === "left" &&
        (numberOfSlides < slidesToShow[breakpoint] || breakpoint === "lg") ? (
          <>
            <VariantContainer
              variants={[
                css`
                  display: grid;
                  grid-template-columns: 1fr;
                  gap: 32px;
                `,
                css`
                  grid-template-columns: 1fr 1fr;
                `,
                css`
                  grid-template-columns: 1fr 1fr 1fr;
                `,
              ]}
            >
              {children}
            </VariantContainer>
            <Spacer height={{ sm: dotsHeight }} />
          </>
        ) : (
          <VariantContainer
            variants={mergeVariants(
              [
                css`
                  .slick-dots {
                    bottom: -${dotsHeight};
                  }
                  margin-bottom: ${dotsHeight};
                  .slick-list {
                    box-sizing: initial;
                    /* Padding for card shadow  */
                    padding: 5px 0 10px 0;
                    /* Margin for left and right spacing & shadows */
                    margin: 0 -12px;
                  }
                  .slick-slide {
                    /* Padding for left and right spacing & shadows */
                    padding: 0 12px;
                    box-sizing: border-box;
                    height: inherit;
                  }
                  .slick-slide > div {
                    height: 100%;
                  }
                  .slick-track {
                    display: flex;
                  }
                `,
                css`
                  /* If we have only 1 card, center and force 362px width
              If we have 2 or more, tablet should let them fill space */
                  .slick-active > div {
                    max-width: ${numberOfSlides === 1 ? `362px` : `auto`};
                  }
                  .slick-current > div {
                    margin: ${numberOfSlides === 1 ? `0 auto` : `auto`};
                  }
                `,
                css`
                  /* If we have less than 3 cards then we need to force them 362px width on desktop */
                  .slick-active > div {
                    max-width: ${numberOfSlides < 3 ? `362px` : `auto`};
                  }
                  /* If we have exactly 2, we need to right justify the left card 
                (this pushes the left and right close together) */
                  .slick-current > div {
                    margin: ${numberOfSlides === 2 ? `0 0 0 auto` : `auto`};
                  }
                  .slick-slide {
                    /* Padding for left and right spacing & shadows */
                    padding: 0 16px;
                    box-sizing: border-box;
                  }
                `,
              ],
              variants
            )}
          >
            <Slider {...(settings || defaultSettings)}>{children}</Slider>
          </VariantContainer>
        )
      ) : (
        /* If client hasn't loaded, simply show the child objects

            In routes without scroll update (blog home) this prevents the scroll position from jumping 
            when the page loads */
        <>{children}</>
      )}
    </>
  )
}

export default CardSlider
